import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PublicFormField } from "@/components/PublicFormField";
import { cn } from "@/lib/utils";

interface FormField {
  id?: string;
  type: string;
  label: string;
  name: string;
  placeholder?: string;
  required?: boolean;
  options?: string[];
  queueField?: string;
}

interface PublicFormData {
  id: string;
  name: string;
  description?: string;
  active: boolean;
  queue_id: string;
  organization_id: string;
  category_id?: string;
  show_organization_picture?: boolean;
  show_animated_background?: boolean;
  background_theme?: string;
  form_fields: FormField[];
  organization: {
    name: string;
    picture_url?: string;
  };
}

interface PublicFormContentProps {
  form: PublicFormData;
  formData: Record<string, any>;
  submitting: boolean;
  onChange: (name: string, value: any) => void;
  onSubmit: (e: React.FormEvent) => void;
}

const getButtonClasses = (theme?: string) => {
  const buttonThemes: Record<string, string> = {
    purple: "bg-purple-600 hover:bg-purple-700 focus:ring-purple-500",
    blue: "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500",
    yellow: "bg-yellow-500 hover:bg-yellow-600 focus:ring-yellow-400",
    green: "bg-green-600 hover:bg-green-700 focus:ring-green-500",
    red: "bg-red-600 hover:bg-red-700 focus:ring-red-500",
  };
  return buttonThemes[theme || "purple"] || buttonThemes.purple;
};

export const PublicFormContent: React.FC<PublicFormContentProps> = ({
  form,
  formData,
  submitting,
  onChange,
  onSubmit,
}) => {
  return (
    <div className="w-full max-w-2xl mx-4">
      <Card>
        <CardHeader>
          <div className="text-center">
            {form.show_organization_picture && form.organization.picture_url && (
              <div className="mb-4">
                <img
                  src={form.organization.picture_url}
                  alt={`${form.organization.name} logo`}
                  className="mx-auto h-16 w-auto object-contain rounded-lg"
                />
              </div>
            )}
            <CardTitle className="text-2xl">{form.name}</CardTitle>
            {form.description && (
              <CardDescription className="mt-2">{form.description}</CardDescription>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-6">
            {form.form_fields.map((field) => (
              <div key={field.id || field.name}>
                <PublicFormField field={field} value={formData[field.name]} onChange={onChange} />
              </div>
            ))}

            <div className="pt-4 border-t">
              <Button
                type="submit"
                className={cn(
                  "w-full text-white border-0 focus:ring-2 focus:ring-offset-2",
                  form?.show_animated_background ? getButtonClasses(form.background_theme) : ""
                )}
                disabled={submitting}
              >
                {submitting ? "Submitting..." : "Submit"}
              </Button>
            </div>

            <div className="text-center pt-4 border-t">
              <span className="text-xs text-muted-foreground">
                Powered by{" "}
                <a
                  href="https://queueos.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  queueOS
                </a>
              </span>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
